import { QueryClient, onlineManager } from '@tanstack/react-query';
import { createMonitoredQueryCache, createMonitoredMutationCache } from './queryErrorHandler';
import { isPermissionError } from './reports/reportState';

export const queryClient = new QueryClient({
  queryCache: createMonitoredQueryCache(),
  mutationCache: createMonitoredMutationCache(),
  defaultOptions: {
    queries: {
      staleTime: 30000,
      // FIN-RPT-FALSE-EMPTY-1: an authorization failure is not a transient one.
      // Retrying it leaves the query pending-but-not-fetching between attempts,
      // which reads as "nothing was asked" — so a denied finance report lingered
      // on "select a company" instead of saying the reader lacks permission.
      // Fail it immediately and let the report render its authorization state.
      retry: (failureCount, error) => !isPermissionError(error) && failureCount < 1,
    },
  },
});

/**
 * FIN-RPT-PAUSED-2 — keep React Query's idea of "online" tied to the browser's.
 *
 * `onlineManager` seeds itself `true` and thereafter moves ONLY on `window`
 * 'online'/'offline' events. It never reads `navigator.onLine`. So a single
 * stray 'offline' event — a captive-portal blip, a devtools network toggle, an
 * 'online' event that arrived while the manager happened to be unsubscribed —
 * latches it false permanently, and every query then PAUSES instead of fetching
 * while the browser reports itself online.
 *
 * That divergence is what a forced HTTP 500 on Production surfaced: the report
 * query sat at `fetchStatus: "paused"` with `navigator.onLine === true`, and the
 * page said there was no network. It also makes a retry button inert, because a
 * refetch under a false manager just pauses again.
 *
 * Re-deriving the value from `navigator.onLine` on every event — and on
 * visibility changes, which is when a returning tab is most likely to be holding
 * a stale one — means the two cannot drift apart. It can only ever correct a
 * stale `false`: when the browser is genuinely offline it reports so, and this
 * agrees with it.
 */
if (typeof window !== 'undefined') {
  onlineManager.setEventListener((setOnline) => {
    const sync = () => setOnline(navigator.onLine !== false);
    sync();
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  });
}
